import React, { useState, useEffect, useMemo } from "react";
import { rtdb } from "../firebase";
import { ref, onValue, push, set, remove } from "firebase/database";
import ProductItem from "./ProductItem";
import "./AdminProduct.css";

export default function AdminProduct({ onBack, email }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [id, setId] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [customer, setCustomer] = useState("");
  const [quantity, setQuantity] = useState("");
  const [supplier, setSupplier] = useState("");

  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Sync with real-time database rows
  useEffect(() => {
    // 🛠 Declared inside the effect to prevent reference recreation loops
    const productsRef = ref(rtdb, "Products");

    const unsubscribe = onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      const list = data
        ? Object.keys(data).map((key) => ({ productId: key, ...data[key] }))
        : [];
      setProducts(list);
      
      // Only flip loading state once to prevent rendering jitters
      if (loading) {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [loading]); // ✅ 'productsRef' is safely encapsulated, resolving the warning

  const handleSave = () => {
    if (!code.trim()) { alert("Please enter a product code"); return; }
    if (!quantity.toString().trim()) { alert("Please enter product quantity"); return; }
    const qty = parseInt(quantity, 10);
    if (isNaN(qty)) { alert("Quantity must be a valid number"); return; }

    const productsRef = ref(rtdb, "Products");
    const productId = id || push(productsRef).key;
    
    const product = {
      code: code.trim(),
      description: description.trim() || "-",
      customer: customer.trim() || "-",
      quantity: qty,
      supplier: supplier.trim() || "-",
    };

    set(ref(rtdb, `Products/${productId}`), product)
      .then(() => {
        alert(id ? "Product updated successfully!" : "New product added!");
        clearForm();
      })
      .catch(() => alert("Failed to save product"));
  };

  const handleEdit = (product) => {
    setId(product.productId || "");
    setCode(product.code || "");
    setDescription(product.description && product.description !== "-" ? product.description : "");
    setCustomer(product.customer && product.customer !== "-" ? product.customer : "");
    setQuantity(product.quantity != null ? String(product.quantity) : "");
    setSupplier(product.supplier && product.supplier !== "-" ? product.supplier : "");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (product) => {
    if (!window.confirm(`Are you sure you want to delete ${product.code}?`)) return;
    
    remove(ref(rtdb, `Products/${product.productId}`))
      .then(() => {
        if (id === product.productId) {
          clearForm();
        }
      })
      .catch(() => alert("Failed to delete product"));
  };

  const clearForm = () => {
    setId(""); setCode(""); setDescription("");
    setCustomer(""); setQuantity(""); setSupplier("");
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = products.filter((p) => {
      if (!q) return true;
      return (
        (p.code || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
      );
    });
    if (sortMode === "az") {
      list = list.slice().sort((a, b) => (a.code || "").localeCompare(b.code || ""));
    } else if (sortMode === "za") {
      list = list.slice().sort((a, b) => (b.code || "").localeCompare(a.code || ""));
    }
    return list;
  }, [products, search, sortMode]);

  return (
    <div className="admin-product-root">
      <header className="dashboard-header">
        <h2 className="page-title-product">Product Management</h2>
      </header>

      <div className="dashboard-container">
        {/* LEFT SECTION: LIST */}
        <div className="main-content">
          <div className="glass-card controls-card">
            <div className="top-controls product-top">
              <div className="search-wrapper">
                <span className="search-icon">🔍</span>
                <input
                  className="search-input"
                  type="text"
                  placeholder="Search by code or description..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="sort-buttons">
                <button className={`sort-btn ${sortMode === "az" ? "active" : ""}`} onClick={() => setSortMode("az")}>A-Z</button>
                <button className={`sort-btn ${sortMode === "za" ? "active" : ""}`} onClick={() => setSortMode("za")}>Z-A</button>
                <button className="sort-btn clear" onClick={() => setSortMode(null)}>Reset</button>
              </div>
            </div>
          </div>

          <div className="list-header glass-header">
            <div className="col code">Code</div>
            <div className="col qty">Qty</div>
            <div className="col desc">Description</div>
            <div className="col cust">Car / Customer</div>
            <div className="col supplier">Supplier</div>
            <div className="expand-indicator-spacer"></div>
          </div>

          <div className="product-list-wrapper">
            {loading ? (
              <div className="loader-container">
                <div className="spinner"></div>
                <p>Syncing with Database...</p>
              </div>
            ) : (
              <div className="product-list">
                {filtered.length === 0 ? (
                  <div className="empty-state">No products match your search.</div>
                ) : (
                  filtered.map((p) => (
                    <ProductItem
                      key={p.productId}
                      product={p}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isExpanded={expandedId === p.productId}
                      onToggle={() => setExpandedId(expandedId === p.productId ? null : p.productId)}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SECTION: FORM */}
        <aside className="side-panel">
          <div className="product-form glass-card">
            <div className="form-header">
              <h3>{id ? "Edit Details" : "Quick Add"}</h3>
              {id && <span className="edit-badge">Editing Mode</span>}
            </div>

            <div className="form-body">
              <div className="input-group">
                <label>Product Code</label>
                <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. P-100" />
              </div>
              <div className="input-group">
                <label>Quantity</label>
                <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" />
              </div>
              <div className="input-group">
                <label>Description</label>
                <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Model details" />
              </div>
              <div className="input-group">
                <label>Customer</label>
                <input type="text" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Assignee" />
              </div>
              <div className="input-group">
                <label>Supplier</label>
                <input type="text" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Source" />
              </div>

              <div className="form-actions">
                <button className="save-btn action-primary" onClick={handleSave}>
                  {id ? "Confirm Changes" : "Create Product"}
                </button>
                {id && <button className="cancel-btn action-secondary" onClick={clearForm}>Cancel</button>}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}